import { createPortal } from "react-dom";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

type Props = {
  open: boolean;
  anchorRect: DOMRect | null;
  onClose: () => void;
  children: React.ReactNode;
};

/**
 * Menu flutuante acoplado ao botão de ações (⋯), renderizado em document.body
 * para não ser recortado por overflow em tabelas/cards.
 */
export function ActionMenuPortal({ open, anchorRect, onClose, children }: Props) {
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, maxHeight: 0, mobile: false });
  const menuRef = useRef<HTMLDivElement>(null);

  const calculatePosition = useCallback(() => {
    if (!open || !anchorRect) return;
    const w = 180;
    const mobile = window.matchMedia("(max-width: 766px)").matches;
    const visualViewport = window.visualViewport;
    const viewportTop = mobile ? visualViewport?.offsetTop ?? 0 : 0;
    const viewportLeft = mobile ? visualViewport?.offsetLeft ?? 0 : 0;
    const viewportWidth = mobile ? visualViewport?.width ?? window.innerWidth : window.innerWidth;
    const viewportHeight = mobile ? visualViewport?.height ?? window.innerHeight : window.innerHeight;
    const viewportBottom = viewportTop + viewportHeight;
    const edge = 8;
    const menuRect = menuRef.current?.getBoundingClientRect();
    const menuHeight = menuRect?.height ?? 0;
    const menuWidth = mobile ? Math.min(menuRect?.width || w, viewportWidth - edge * 2) : 0;
    const maxHeight = Math.max(80, viewportHeight - edge * 2);
    const boundedMenuHeight = Math.min(menuHeight, maxHeight);
    const spaceBelow = viewportBottom - edge - anchorRect.bottom - 4;
    const spaceAbove = anchorRect.top - 4 - (viewportTop + edge);
    const openAbove = mobile && spaceBelow < boundedMenuHeight && spaceAbove > spaceBelow;
    const top = mobile
      ? Math.max(viewportTop + edge, Math.min(
          openAbove ? anchorRect.top - boundedMenuHeight - 4 : anchorRect.bottom + 4,
          viewportBottom - edge - boundedMenuHeight,
        ))
      : anchorRect.bottom + 4;
    const left = mobile
      ? Math.max(viewportLeft + edge, Math.min(anchorRect.right - menuWidth, viewportLeft + viewportWidth - menuWidth - edge))
      : Math.max(8, Math.min(anchorRect.right - w, window.innerWidth - w - 8));
    setPos({ top, left, width: menuWidth, maxHeight, mobile });
  }, [open, anchorRect]);

  useLayoutEffect(() => {
    calculatePosition();
  }, [calculatePosition]);

  useLayoutEffect(() => {
    if (!open) return;
    const onScroll = () => onClose();
    window.addEventListener("scroll", onScroll, true);
    const mobile = window.matchMedia("(max-width: 766px)").matches;
    const onResize = () => mobile ? calculatePosition() : onClose();
    window.addEventListener("resize", onResize);
    if (mobile) {
      window.visualViewport?.addEventListener("resize", calculatePosition);
      window.visualViewport?.addEventListener("scroll", calculatePosition);
    }
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", calculatePosition);
      window.visualViewport?.removeEventListener("scroll", calculatePosition);
    };
  }, [open, onClose, calculatePosition]);

  if (!open || !anchorRect || typeof document === "undefined") return null;

  return createPortal(
    <>
      <div
        data-action-menu-backdrop
        className="fixed inset-0 z-[500]"
        aria-hidden
        onMouseDown={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        ref={menuRef}
        data-action-menu-portal
        className="fixed z-[510] min-w-[140px] rounded-md border border-gray-200 bg-white py-1 text-gray-900 shadow-xl"
        style={{
          top: pos.top,
          left: pos.left,
          ...(pos.mobile ? { width: pos.width, maxWidth: "calc(100vw - 16px)", maxHeight: pos.maxHeight, overflowY: "auto", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" } : {}),
        }}
        onMouseDown={(e) => e.stopPropagation()}
        role="menu"
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
